import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { TextField } from '@zep-us/zus';
import { QUIZ_TERM_OPTIONS, QUIZ_USER_COUNT_MAX } from '../consts/quizQuote.const';
import { useApplicableFirstPurchasePromotion } from '../hooks/useApplicableFirstPurchasePromotion';
import { useApplicableTrialEndDiscount } from '../hooks/useApplicableTrialEndDiscount';
import { useQuotePlanSelection } from '../hooks/useQuotePlanSelection';
import { type QuizQuoteFormValues } from '../utils/quizQuote.schema';
import { type FirstPurchasePromotion, type TrialEndQuoteDiscount } from '../utils/quoteCalculator';
import { type QuotePricingPolicy } from '../utils/quotePricingPolicy';
import { BasicPlanTypeSection } from './BasicPlanTypeSection';
import { NumberStepperField } from './NumberStepperField';
import { PlanSelectSection } from './PlanSelectSection';
import { PlanTypeSection } from './PlanTypeSection';
import { QuoteDateField } from './QuoteDateField';
import { showQuoteRestrictionToast } from './QuoteRestrictionToast';
import { QuoteSection } from './QuoteSection';
import { QuoteSelectField } from './QuoteSelectField';
import { UsagePeriodPreview } from './UsagePeriodPreview';

// Chrome autofill 이 인풋(패딩 안쪽)에만 배경을 칠해 필드 패딩에 빈틈이 생기는 문제 방지.
// autofill 배경을 필드색(흰색)으로 덮어 필드 전체가 균일하게 보이도록 한다.
const AUTOFILL_RESET = ['[&_input:-webkit-autofill]:[-webkit-text-fill-color:#27262E]', '[&_input:-webkit-autofill]:[-webkit-box-shadow:0_0_0_1000px_#fff_inset]', '[&_input:-webkit-autofill:focus]:[-webkit-box-shadow:0_0_0_1000px_#fff_inset]'].join(' ');
export function QuizQuoteForm({
  policy,
  firstPurchasePromotion,
  trialEndDiscount = null
}: {
  policy: QuotePricingPolicy | null;
  firstPurchasePromotion: FirstPurchasePromotion | null;
  trialEndDiscount?: TrialEndQuoteDiscount | null;
}) {
  const applicableTrialEndDiscount = useApplicableTrialEndDiscount(trialEndDiscount);
  const applicableFirstPurchasePromotion = useApplicableFirstPurchasePromotion(firstPurchasePromotion);
  const {
    register,
    control,
    getValues,
    setValue,
    formState: {
      errors
    }
  } = useFormContext<QuizQuoteFormValues>();
  const plan = useWatch<QuizQuoteFormValues, 'plan'>({
    name: 'plan'
  });
  const planType = useWatch<QuizQuoteFormValues, 'plan_type'>({
    name: 'plan_type'
  });
  const isGroupPlan = planType === 'group';
  const {
    selectPlan,
    selectPlanType
  } = useQuotePlanSelection({
    plan,
    planType,
    getValues,
    setValue,
    policy
  });
  const minGroupTerm = policy?.groupMinMonthCount;
  const minGroupUsers = policy?.groupMinUserCount;
  const termOptions = QUIZ_TERM_OPTIONS.map(option => {
    const isUnavailableGroupTerm = isGroupPlan && minGroupTerm !== undefined && Number(option.value) < minGroupTerm;
    return {
      ...option,
      label: isUnavailableGroupTerm ? <span className="text-disabled">{option.label}</span> : option.label
    };
  });
  return <div className={`flex w-full flex-col gap-lg ${AUTOFILL_RESET}`} data-sentry-component="QuizQuoteForm" data-sentry-source-file="QuizQuoteForm.tsx">
      <PlanSelectSection value={plan} onChange={selectPlan} data-sentry-element="PlanSelectSection" data-sentry-source-file="QuizQuoteForm.tsx" />

      {plan === 'pro' ? <PlanTypeSection value={planType} onChange={selectPlanType} policy={policy} trialEndDiscount={trialEndDiscount} isDiscountApplicable={applicableTrialEndDiscount !== null} /> : <BasicPlanTypeSection policy={policy} firstPurchasePromotion={firstPurchasePromotion} isDiscountApplicable={applicableFirstPurchasePromotion !== null} />}

      <QuoteSection title="기본 정보" data-sentry-element="QuoteSection" data-sentry-source-file="QuizQuoteForm.tsx">
        <TextField.Root error={!!errors.school_name} required data-sentry-element="unknown" data-sentry-source-file="QuizQuoteForm.tsx">
          <TextField.Label data-sentry-element="unknown" data-sentry-source-file="QuizQuoteForm.tsx">학교(기업)명</TextField.Label>
          <TextField.Description data-sentry-element="unknown" data-sentry-source-file="QuizQuoteForm.tsx">
            학교의 경우 지역명까지 포함하여 정확하게 입력해주세요. ex)
            서울경기초등학교
          </TextField.Description>
          <TextField.Input placeholder="견적서를 발행할 학교(기업)명을 입력해 주세요" error={!!errors.school_name} {...register('school_name')} data-sentry-element="unknown" data-sentry-source-file="QuizQuoteForm.tsx" />
          {errors.school_name ? <TextField.HelperText>
              {errors.school_name.message}
            </TextField.HelperText> : null}
        </TextField.Root>

        <TextField.Root error={!!errors.account_name} required data-sentry-element="unknown" data-sentry-source-file="QuizQuoteForm.tsx">
          <TextField.Label data-sentry-element="unknown" data-sentry-source-file="QuizQuoteForm.tsx">담당자</TextField.Label>
          <TextField.Input placeholder="담당자 성함을 입력해 주세요" error={!!errors.account_name} {...register('account_name')} data-sentry-element="unknown" data-sentry-source-file="QuizQuoteForm.tsx" />
          {errors.account_name ? <TextField.HelperText>
              {errors.account_name.message}
            </TextField.HelperText> : null}
        </TextField.Root>

        <TextField.Root error={!!errors.account_phone} required data-sentry-element="unknown" data-sentry-source-file="QuizQuoteForm.tsx">
          <TextField.Label data-sentry-element="unknown" data-sentry-source-file="QuizQuoteForm.tsx">담당자 번호</TextField.Label>
          <TextField.Input type="tel" inputMode="tel" placeholder="담당자 휴대폰 번호를 입력해 주세요" error={!!errors.account_phone} {...register('account_phone')} data-sentry-element="unknown" data-sentry-source-file="QuizQuoteForm.tsx" />
          {errors.account_phone ? <TextField.HelperText>
              {errors.account_phone.message}
            </TextField.HelperText> : null}
        </TextField.Root>

        <TextField.Root error={!!errors.account_email} required data-sentry-element="unknown" data-sentry-source-file="QuizQuoteForm.tsx">
          <TextField.Label data-sentry-element="unknown" data-sentry-source-file="QuizQuoteForm.tsx">담당자 이메일</TextField.Label>
          <TextField.Input type="email" inputMode="email" placeholder="담당자 이메일을 입력해 주세요" error={!!errors.account_email} {...register('account_email')} data-sentry-element="unknown" data-sentry-source-file="QuizQuoteForm.tsx" />
          {errors.account_email ? <TextField.HelperText>
              {errors.account_email.message}
            </TextField.HelperText> : null}
        </TextField.Root>
      </QuoteSection>

      <QuoteSection title="플랜 사용일 선택" data-sentry-element="QuoteSection" data-sentry-source-file="QuizQuoteForm.tsx">
        <Controller control={control} name="term" render={({
        field
      }) => <QuoteSelectField label="사용 기간" options={termOptions} value={String(field.value)} onValueChange={value => {
        if (value) {
          if (isGroupPlan && minGroupTerm !== undefined && Number(value) < minGroupTerm) {
            showQuoteRestrictionToast(`선택한 요금제는 최소 ${minGroupTerm}개월부터 이용할 수 있어요.`);
            return;
          }
          field.onChange(Number(value));
        }
      }} placeholder="사용 기간 선택" />} data-sentry-element="Controller" data-sentry-source-file="QuizQuoteForm.tsx" />
        <Controller control={control} name="start_date" render={({
        field
      }) => <QuoteDateField label="사용 시작일" value={field.value} onChange={field.onChange} />} data-sentry-element="Controller" data-sentry-source-file="QuizQuoteForm.tsx" />
        <UsagePeriodPreview data-sentry-element="UsagePeriodPreview" data-sentry-source-file="QuizQuoteForm.tsx" />
      </QuoteSection>

      <QuoteSection title="사용 인원 선택" data-sentry-element="QuoteSection" data-sentry-source-file="QuizQuoteForm.tsx">
        <Controller control={control} name="user_count" render={({
        field
      }) => <NumberStepperField label="사용 인원" value={field.value} onChange={field.onChange} min={isGroupPlan ? minGroupUsers ?? 1 : 1} max={QUIZ_USER_COUNT_MAX} onMinClick={isGroupPlan && minGroupUsers !== undefined ? () => showQuoteRestrictionToast(`선택한 요금제는 최소 ${minGroupUsers}명부터 이용할 수 있어요.`) : undefined} />} data-sentry-element="Controller" data-sentry-source-file="QuizQuoteForm.tsx" />
      </QuoteSection>

      <QuoteSection title="기타" data-sentry-element="QuoteSection" data-sentry-source-file="QuizQuoteForm.tsx">
        <TextField.Root error={!!errors.annotation} data-sentry-element="unknown" data-sentry-source-file="QuizQuoteForm.tsx">
          <TextField.Label optionalText="" data-sentry-element="unknown" data-sentry-source-file="QuizQuoteForm.tsx">비고 (선택)</TextField.Label>
          <TextField.Input placeholder="비고 내용을 입력해주세요" error={!!errors.annotation} {...register('annotation')} data-sentry-element="unknown" data-sentry-source-file="QuizQuoteForm.tsx" />
        </TextField.Root>
      </QuoteSection>
    </div>;
}